import type {IWishlistPayload} from '~/utils/services/api/account/wishlists/wishlists';
import {getWishlists, createWishlist, updateWishlist, deleteWishlist} from '~/utils/services/api/account/wishlists/wishlists';
import {useCpUser} from '~/composables/useCpUser';
import {QUERY_KEYS} from '~/utils/services/keys';
import {getNonReviewedProducts, getDownloads} from '~/utils/services/api/account/account';
import {rejectQueryFn} from '~/utils';
import {addAddress, deleteAddress, editAddress, getAddresses, getSingleAddress, verifyAddress} from '~/utils/services/api/account/addresses/addresses';
import {
    addBillingData,
    deleteBillingData, editBillingData,
    getBillingData, selectBillingData,
} from '~/utils/services/api/account/billing-data/billing-data';
import type {IBillingDataItemUpdate, ICpAddress} from '~/types';
import type {updateAccountNameParams, updatePublicProfilePayload} from '~/utils/services/api/account/publicProfile';
import {
    getPublicProfile,
    updateAccountUserName,
    updatePublicProfile,
} from '~/utils/services/api/account/publicProfile';
import type {ICreateOrderResponse, ICreateOrderOptions, ICreateReturnCase, IUpdateReturnCase} from '~/utils/services/api/account/orders';
import {
    getOrders,
    sendOrdersConfirmationMail,
    getOrdersPreviewMail,
    cancelOrder,
    createOrder,
    getOrder,
    getTrackingCodes,
    getOrderPaymentBlock,
    getOrderOverdueProducts,
    cancelOverdueProducts,
    regenerateWaybillByReturnCase,
    regenerateWaybillByOrder,
    archiveReturnCase,
    createReturnCase,
    getProportionalDeliveryCost,
    updateReturnCase,
    getOrderReturnCases,
    getArticlesPerWaybill,
    getPaymentInstructions,
} from '~/utils/services/api/account/orders';
import {getTroubleshootingGuides} from '~/utils/services/api/catalog/catalog';

export interface ICreateOrderError extends Error {
    data: {
        msg: string;
        code: number;
        errors: {
            status: string;
            code: string;
            title: string;
            detail: string;
        }[];
        meta: {
            messagesToDisplay: {
                title: string;
                body: string;
                type: string;
            }[];
        };
    }
}

export const useNonReviewedProducts = (page?: number, immediate = true) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.nowReviewedProducts, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return getNonReviewedProducts(userEmail, page);
        }
    }, {immediate});
};

export const usePublicProfile = () => useAsyncData(QUERY_KEYS.account.publicProfile, () => getPublicProfile());
export const useUpdatePublicProfile = (payload: updatePublicProfilePayload) => useAsyncData(QUERY_KEYS.account.publicProfile, () => updatePublicProfile(payload));

export const useUpdateUserName = (payload: updateAccountNameParams) => {
    const {userId} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.user(userId || ''), () => {
        if (userId) {
            return updateAccountUserName(payload);
        }
        return rejectQueryFn();
    });
};

export const useWishlists = (server = true) => {
  const {userId} = useCpUser();
  return useAsyncData(QUERY_KEYS.account.wishlists, () => {
    // We need a user logged in to make this request
    if (userId) {
        return getWishlists();
    }
    return rejectQueryFn();
  }, {server});
};

export const useCreateWishlist = (payload: IWishlistPayload) => {
  return useAsyncData(QUERY_KEYS.account.createWishlist, () => createWishlist(payload));
};

export const useUpdateWishlist = (payload: IWishlistPayload) => {
  return useAsyncData(QUERY_KEYS.account.updateWishlist, () => updateWishlist(payload));
};

export const useDeleteWishlist = () => {
  return useAsyncData(QUERY_KEYS.account.deleteWishlist, () => deleteWishlist());
};

export const useAddresses = (immediate = true) => {
    return useAsyncData(QUERY_KEYS.account.addresses, () => {
        const {userEmail} = useCpUser();
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return getAddresses(userEmail);
        }
    }, {immediate, lazy: true});
};

export const useAddAddress = (address: ICpAddress) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.createAddresses, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return addAddress(userEmail, address);
        }
    });
};

export const useEditAddress = (addressId: string, address: ICpAddress, justSetAsDefault = false) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.updateAddresses, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return editAddress(userEmail, {addressId, address}, justSetAsDefault);
        }
    });
};

export const useDeleteAddress = (addressId: string) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.deleteAddresses, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return deleteAddress(userEmail, addressId);
        }
    });
};

export const useBillingData = () => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.billingData, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
           return getBillingData(userEmail);
        }
    });
};

export const useAddBillingData = (billingData: IBillingDataItemUpdate) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.createBillingData, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return addBillingData(userEmail, billingData);
        }
    });
};

export const useEditBillingData = (billingDataId: string, billingData: IBillingDataItemUpdate) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.updateBillingData, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return editBillingData(userEmail, {billingData, billingDataId});
        }
    });
};

export const useDeleteBillingData = (idBillingData: string) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.deleteBillingData, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return deleteBillingData(userEmail, idBillingData);
        }
    });
};

export const useSelectBillingData = (idBillingData: string, status = true) => {
    const {userEmail} = useCpUser();
    return useAsyncData(QUERY_KEYS.account.selectBillingData, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return selectBillingData(userEmail, idBillingData, status);
        }
    });
};

export const useOrder = (idOrder: string | undefined) => {
    return useAsyncData(QUERY_KEYS.account.orders(idOrder), () => {
        if (idOrder) {
 return getOrder(idOrder);
} else {
 return rejectQueryFn();
}
    });
};

export const useOrders = (page = 1) => useAsyncData(QUERY_KEYS.account.orders(), () => getOrders(page));

export const useTrackingCodes = (orderId: string | undefined) => {
    return useAsyncData(QUERY_KEYS.account.orderTracking(orderId), () => {
        if (orderId) {
 return getTrackingCodes(orderId);
} else {
 return rejectQueryFn();
}
    });
};

export const useSendOrderConfirmationMail = (email: string) => useAsyncData(QUERY_KEYS.account.ordersConfirmationMail, () => sendOrdersConfirmationMail(email));

export const useGetOrderPreviewMail = () => useAsyncData(QUERY_KEYS.account.ordersPreviewEmail, () => getOrdersPreviewMail());

export const useCancelOrder = () => useAsyncData(QUERY_KEYS.account.cancelOrder, () => cancelOrder());

export const useDownloads = () => {
    const {userEmail} = useCpUser();
    const pageParam = 1;
    return useAsyncData(QUERY_KEYS.account.downloads, () => {
        if (!userEmail) {
            return rejectQueryFn();
        } else {
            return getDownloads(userEmail, {pageNumber: pageParam});
        }
    });
};

export const useCreateOrder = (paymentMethod: string | null | undefined, options: ICreateOrderOptions = {jumio_flag: 0}) =>
    useAsyncData<ICreateOrderResponse, ICreateOrderError>(QUERY_KEYS.account.createOrder, () => createOrder(paymentMethod, options));

export const useSingleOrder = (id: string) => useAsyncData(QUERY_KEYS.account.orders(id), () => getOrder(id));

export const useGetSingleAddress = (id: string) => useAsyncData(QUERY_KEYS.account.address(id), () => getSingleAddress(id));

export const useVerifyAddress = (id: string, code: string) => useAsyncData(QUERY_KEYS.account.address(id), () => verifyAddress(id, code));

export const useGetPaymentBlock = (orderId: string | null) => useAsyncData(QUERY_KEYS.account.paymentBlock, () => orderId ? getOrderPaymentBlock(orderId) : rejectQueryFn());

export const useGetOrderOverdueProducts = (orderId: string | undefined) => useAsyncData(QUERY_KEYS.account.ordersOverdueProducts, () => orderId ? getOrderOverdueProducts(orderId) : rejectQueryFn());

export const useCancelOverdueProducts = (orderId: string | undefined, articles: Array<{ id: string, amount: number }>) => useAsyncData(QUERY_KEYS.orders.cancelExpiredArticles(orderId),
    () => orderId ? cancelOverdueProducts(orderId, articles) : rejectQueryFn());

export const useRegenerateWaybillByReturnCase = (orderId: string | undefined, returnCaseId: string | undefined) => useAsyncData(QUERY_KEYS.orders.regenerateWaybillByReturnCase(orderId, returnCaseId),
() => orderId && returnCaseId ? regenerateWaybillByReturnCase(orderId, returnCaseId) : rejectQueryFn());

export const useRegenerateWaybillByOrder = (orderId: string | undefined) => useAsyncData(QUERY_KEYS.orders.regenerateWaybillByOrder(orderId), () => orderId ? regenerateWaybillByOrder(orderId) : rejectQueryFn());

export const useArchiveReturnCase = (orderId: string | undefined, returnCaseId: string | undefined, comments = '') => useAsyncData(QUERY_KEYS.orders.archiveReturnCase(orderId, returnCaseId), () => orderId && returnCaseId ? archiveReturnCase(orderId, returnCaseId, comments) : rejectQueryFn());

export const useCreateReturnCase = (orderId: string | undefined, payload: ICreateReturnCase) => useAsyncData(QUERY_KEYS.orders.createReturnCase(payload.article), () =>
    orderId ? createReturnCase(orderId, payload) : rejectQueryFn());

export const useUpdateReturnCase = (orderId: string | undefined, payload: IUpdateReturnCase, returnCaseId: string | undefined) => useAsyncData(QUERY_KEYS.orders.updateReturnCase(returnCaseId), () => orderId && returnCaseId ? updateReturnCase(orderId, payload, returnCaseId) : rejectQueryFn());

export const useGetProportionalDeliveryCost = (orderId: string | undefined, articlesIds: Array<string>) => useAsyncData(QUERY_KEYS.account.proportionalDeliveryCost(orderId), () => orderId ? getProportionalDeliveryCost(orderId, articlesIds) : rejectQueryFn());

export const useGetOrderReturnCases = (orderId: string | undefined, returnCaseId?: string, articleSKU?: string) => useAsyncData(QUERY_KEYS.account.returnCases(orderId), () => orderId ? getOrderReturnCases(orderId, returnCaseId, articleSKU) : rejectQueryFn());

export const useGetArticlesPerWaybill = (orderId: string | undefined) => useAsyncData(QUERY_KEYS.orders.articlesPerWaybill(orderId), () => orderId ? getArticlesPerWaybill(orderId) : rejectQueryFn());

export const useGetPaymentInstructions = (orderId: string | undefined) => useAsyncData(QUERY_KEYS.orders.paymentInformation(orderId), () => orderId ? getPaymentInstructions(orderId) : rejectQueryFn());

export const useGetTroubleshootingGuides = (categoryId: string) => useAsyncData(QUERY_KEYS.catalog.troubleshootingGuides(categoryId), () => categoryId ? getTroubleshootingGuides(categoryId) : rejectQueryFn());
